import { isDefined, isNotDefined } from '@sgme/fp';
import { createSafeContext } from '@sgme/react';
import { DealSplit } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/fields/DealSplitField';
import { ProductSummaryScheduleRow } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/fields/Schedule/Schedule.model';
import { ProductSummaryScheduleDatesRow } from 'components/common-form/sidePanel/docToolbar/summary/builder/node/fields/ScheduleDates/ScheduleDate.model';
import { TenorFieldData } from 'components/shared/TenorField/TenorField';
import { DistributionCountries } from 'models/DistributionCountry';
import { MultipleUnitsFieldData } from 'models/forms/fields/MultipleUnitsFieldMetadata';
import { PresetDateFieldData } from 'models/forms/fields/PresetDateFieldMetadata';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import type {
  ReadonlyValueDefinition,
  ReadonlyValueDefinitionByType,
  ReadonlyValueDefinitionFieldType,
} from 'models/metadata/readonlyValue.definition';
import { Underlying } from 'models/underlying';
import { PropsWithChildren } from 'react';
import { SolvingModeDetail } from 'state/quotes/details/details.model';
import { Collection } from 'state/utils/types';

type ContextType = {
  getData: <TData>(fieldId: string) => TData | undefined;
  getMetadataWithData: <TFieldType extends ReadonlyValueDefinitionFieldType>(
    metadata: ReadonlyValueDefinitionByType<TFieldType>,
  ) => DataByType<TFieldType> | undefined;
};
export const [Provider, useReadonlyField] = createSafeContext<ContextType>('ReadonlyFieldMetadataContext', {
  getMetadataWithData: () => undefined,
  getData: () => undefined,
});

interface Props extends PropsWithChildren {
  data: Collection<Readonly<unknown>>;
}
export function ReadonlyFieldProvider({ data, children }: Props) {
  const getMetadataWithData = <TFieldType extends ReadonlyValueDefinitionFieldType>(
    metadata: ReadonlyValueDefinitionByType<TFieldType>,
  ) => {
    if (isNotDefined(metadata)) {
      return undefined;
    }
    const result = getControlledData(metadata, data) as DataByType<TFieldType>;
    return result;
  };
  function getData<TData>(field: string) {
    return data[field] as TData | undefined;
  }
  return <Provider value={{ getMetadataWithData, getData }}>{children}</Provider>;
}

type AmountData = { unit?: string; value: number };

export type DataByType<T extends ReadonlyValueDefinitionFieldType> = T extends 'Amount'
  ? AmountData
  : T extends 'AdditionalCalendars'
    ? ReadonlyArray<string | { Calendar: string }>
    : T extends 'Client'
      ? ISalesCounterpart
      : T extends 'Date'
        ? string | undefined
        : T extends 'PresetDate'
          ? PresetDateFieldData | undefined
          : T extends 'DatesList'
            ? string[] | undefined
            : T extends 'DealSplitGrid'
              ? DealSplit[]
              : T extends 'FixedUnit' | 'Number'
                ? number | undefined
                : T extends 'FixedUnitWithPostValue'
                  ? { postValue?: number; value: number | undefined }
                  : T extends 'FixedUnitWithPreValue'
                    ? { preValue?: number; value: number | undefined }
                    : T extends 'Text' | 'SolvingMode'
                      ? string
                      : T extends 'TextList'
                        ? string[] | undefined
                        : T extends 'DistributionCountryGrid'
                          ? DistributionCountries | undefined
                          : T extends 'Tenor'
                            ? TenorFieldData | undefined
                            : T extends 'TextWithDoublePostValues'
                              ? { postValue?: string; postValue2?: string; value: string | undefined }
                              : T extends 'TextWithPostValue'
                                ? { postValue?: string; value: string | undefined }
                                : T extends 'Toggle'
                                  ? boolean | undefined
                                  : T extends 'Underlyings'
                                    ? Underlying[] | undefined
                                    : T extends 'Underlying'
                                      ? Underlying | undefined
                                      : T extends 'Product'
                                        ? { productType: string; productSubtype: string }
                                        : T extends 'Sensitivity'
                                          ? { value: number; solveFor: string }
                                          : T extends 'SwapScheduleGrid' | 'SwapScheduleGrid'
                                            ? ProductSummaryScheduleRow[]
                                            : T extends 'ScheduleDatesGrid'
                                              ? ProductSummaryScheduleDatesRow[]
                                              : T extends 'MultipleUnits'
                                                ? MultipleUnitsFieldData
                                                : undefined;
function getControlledData(
  metadata: ReadonlyValueDefinition,
  data: Collection<Readonly<unknown>>,
): DataByType<typeof metadata.type> {
  switch (metadata.type) {
    case 'Amount':
      const unit = (isDefined(metadata.unitId) ? data[metadata.unitId] : undefined) as string | undefined;
      return { unit, value: data[metadata.id] as number };

    case 'AdditionalCalendars':
      return data[metadata.id] as ReadonlyArray<string | { Calendar: string }>;
    case 'Client':
      return data[metadata.id] as ISalesCounterpart;
    case 'Date':
      return data[metadata.id] as string | undefined;
    case 'PresetDate':
      return data[metadata.id] as PresetDateFieldData | undefined;
    case 'DatesList':
      return data[metadata.id] as string[] | undefined;
    case 'DealSplitGrid':
      return data[metadata.id] as DealSplit[];
    case 'FixedUnit':
    case 'Number':
      return data[metadata.id] as number | undefined;
    case 'FixedUnitWithPostValue':
      const postValue = isDefined(metadata.postValue) ? (data[metadata.postValue] as number | undefined) : undefined;
      return { postValue, value: data[metadata.id] as number | undefined };
    case 'FixedUnitWithPreValue':
      const preValue = isDefined(metadata.preValue) ? (data[metadata.preValue] as number | undefined) : undefined;
      return { preValue, value: data[metadata.id] as number | undefined };
    case 'Text':
      return isDefined(data[metadata.id]) ? String(data[metadata.id]) : undefined;
    case 'TextList':
      return data[metadata.id] as string[] | undefined;
    case 'SolvingMode':
      const solvingMode = data[metadata.id] as SolvingModeDetail | undefined;
      return solvingMode?.label ?? solvingMode?.id;
    case 'DistributionCountryGrid':
      return data[metadata.id] as DistributionCountries | undefined;
    case 'Tenor':
      return data[metadata.id] as TenorFieldData | undefined;
    case 'TextWithDoublePostValues':
      return {
        postValue: isDefined(metadata.postValue) ? (data[metadata.postValue] as string | undefined) : undefined,
        postValue2: isDefined(metadata.postValue2) ? (data[metadata.postValue2] as string | undefined) : undefined,
        value: data[metadata.id] as string | undefined,
      };
    case 'TextWithPostValue':
      return {
        postValue: isDefined(metadata.postValue) ? (data[metadata.postValue] as string | undefined) : undefined,
        value: data[metadata.id] as string | undefined,
      };
    case 'Toggle':
      return data[metadata.id] as boolean | undefined;
    case 'Underlyings':
      return data[metadata.id] as Underlying[] | undefined;
    case 'Underlying':
      return data[metadata.id] as Underlying | undefined;
    case 'Product':
      const productType = data[metadata.productType] as string | undefined;
      const productSubtype = data[metadata.id] as string | undefined;
      return isDefined(productType) && isDefined(productSubtype) ? { productType, productSubtype } : undefined;

    case 'Sensitivity':
      const value = data[metadata.id] as number | undefined;
      const solveFor = getControlledData(metadata.solvingMode, data) as DataByType<'SolvingMode'>;

      return isDefined(value) && isDefined(solveFor)
        ? {
            value: data[metadata.id] as number,
            solveFor,
          }
        : undefined;
    case 'SwapScheduleGrid':
    case 'ExerciseScheduleGrid':
      return data[metadata.id] as ProductSummaryScheduleRow[];
    case 'ScheduleDatesGrid':
      return data[metadata.id] as ProductSummaryScheduleDatesRow[];
    case 'MultipleUnits':
      return data[metadata.id] as MultipleUnitsFieldData;
    default:
      return undefined;
  }
}
